/**
 * Microphone permission for answering WhatsApp calls in Utazzo.
 *
 * Asked up front (when phone notifications are switched on) so a ringing call
 * isn't held up by a "Allow microphone?" prompt - every second counts before
 * the ring ends. Everything here answers quietly instead of throwing.
 */

export type MicState = 'granted' | 'denied' | 'prompt' | 'unknown';

const hasGetUserMedia = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';

/** Current state, without prompting. `unknown` where the browser won't say (older Safari). */
export const micPermission = async (): Promise<MicState> => {
  if (!hasGetUserMedia()) return 'denied';
  try {
    const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
    const s = status?.state;
    return s === 'granted' || s === 'denied' || s === 'prompt' ? s : 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * Ask for the microphone and let go of it straight away - only the permission
 * is wanted now, not the audio. True when allowed.
 */
export const requestMicrophone = async (): Promise<boolean> => {
  if (!hasGetUserMedia()) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
};

/** Ask only when it would actually prompt (or we can't tell); never nag after a "Block". */
export const primeMicrophone = async (): Promise<void> => {
  const state = await micPermission();
  if (state === 'prompt' || state === 'unknown') await requestMicrophone();
};
